/**
 * @file   src\Components\StripeUpdatePaymentCard.tsx
 * @brief  StripeUpdatePaymentCard Component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Button, useState, Link, useEffect, Collapse, useNavigate, useSearchParams } from '../utils/thirdpartyComponents';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Gobackicon from "../assets/images/left-arrow-small.svg";
import CWInput from "src/components/CWInput";
import Cardicon from "../assets/images/credit-card-icon.svg";
import Addicon from "../assets/images/add-new-card-icon.svg";
import { NAME_LENGTH } from '../utils/constants';
import * as Yup from 'yup'
import { pageURLs } from '../utils/pageUrls';
import "src/styles/inner-page-styles.scss";
import { formatCreditCardNumber, formatExpirationDate } from "../utils/helper"
import { IPaymentform } from '../interfaces/generalInterface';
import { UpdatePaymentApi } from 'src/utils/apiUrls';
import axios from 'src/config/axiosConfig';
import { toast } from 'react-toastify';
import SmallLoader from './SmallLoader';
import { MessageToaster } from "src/utils/toastUtil";
import FullLoaderwithText from './FullLoaderwithText';
import EditCardModal from './EditCardModal';

interface CustomCardProps {
    headerText: string;
    contentText: string;
    footerText: string;
    customerId: string;
    paymentMethod : string;
}

interface Card {
    cardId: string;
    last4: string;
    display_brand: string;
    exp_month: number;
    exp_year: number;
    default: boolean;
  }

const StripeUpdatePaymentCard: React.FC<CustomCardProps> = ({ headerText, contentText, footerText, customerId , paymentMethod}) => {
    const stripe = useStripe();
    const elements = useElements();
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Initialize useNavigate
    const navigate = useNavigate();



  
    const cardTypeOption = {
        showIcon: true,
    };

    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [cardErrors, setCardErrors] = useState<{ custcardnumber?: string; custexpirydate?: string; custcvv?: string, custcardname?: string }>({});
    const [success, setSuccess] = useState(false);
    const [statusCheckingLoader,setStatusCheckingLoader] = useState<boolean>(false);

    // Initialize states for formdata
    const [formData, setFormData] = useState<IPaymentform>({
        custcardnumber: '',
        custexpirydate: '',
        custcvv: 0,
        custcardname: '',
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        custcardnumber: '',
        custexpirydate: '',
        custcvv: '',
        custcardname: '',
    });


    // State to saved card details
    const [savedcards, setSavedcards] = useState<Card[]>([]); 

    // To get list of saved cards
    const getSavedCards = async () => {
    try {
        setLoading(true);
        const { data } = await axios.get(UpdatePaymentApi.GET_SAVED_CARDS_LIST);
    
        if (data.error === 0) {
            setIsnewadded(false)
        if (data.data.paymentData.length === 0) {
            setOpen(true);
        } else {
            const cardList: Card[] = data.data.paymentData.map((card: Card) => ({
            cardId: card.cardId,
            last4: card.last4,
            display_brand: card.display_brand,
            exp_month: card.exp_month,
            exp_year: card.exp_year,
            default: card.default,
            }));
            
            setSavedcards(cardList);
        }
        }
    
        setLoading(false);
    } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
    }
    };

    useEffect(() => {
        getSavedCards();
    }, []);

    const handleCardNumberChange = (event: any) => {
        setCardErrors((prevErrors) => ({ ...prevErrors, custcardnumber: event.error ? event.error.message : '' }));
 
    };

    const handleCardExpiryChange = (event: any) => {
   
        setCardErrors((prevErrors) => ({ ...prevErrors, custexpirydate: event.error ? event.error.message : '' }));
    };

    const handleCardCvcChange = (event: any) => {
  
        setCardErrors((prevErrors) => ({ ...prevErrors, custcvv: event.error ? event.error.message : '' }));
    };

    const [isdefaultcard, setIsdefaultcard] = useState(1);
    // Checkbox change handler
    const handleCheckboxChange = (checked: boolean) => {
        // Update form data with the checkbox state
        setIsdefaultcard(checked === true ? 1 : 0);
    };
    const handleSubmit = async (event: React.FormEvent) => {
        if (open) {
            try {

                event.preventDefault();
               

                if (!stripe || !elements) {
                    return;
                }

                const cardNumberElement = elements.getElement(CardNumberElement);
                const cardExpiryElement = elements.getElement(CardExpiryElement);
                const cardCvcElement = elements.getElement(CardCvcElement);
                if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
                    return;
                }
                setLoading(true);
                const { error, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardNumberElement,
                    billing_details: {
                        name: formData.custcardname,
                    }
                });
                if (error) {
                    setLoading(false)
                    // setError(error.message)
                    // setIsProcessing(false);
                    return;
                }
                else
                {
                    const addcard_detail_data = {
                        paymentMethodId: paymentMethod?.id,
                        isDefault: isdefaultcard,
                        action: "add",
                        exp_month: 0,
                        exp_year: 0,
                    };
                    // API call to add card details
                    try {
                        setLoading(true);
                        toaster.toastDismiss();
                        const { data } = await axios.post(UpdatePaymentApi.UPDATE_CARD_DETAILS,addcard_detail_data);
                        if(data?.error === 0)
                        {
                            // hide form
                            setOpen(false);
                            // clear form
                            setFormData({
                                custcardnumber: '',
                                custexpirydate: '',
                                custcvv: 0,
                                custcardname: '',
                            });
                            setIsnewadded(true);
                            setResetKey((prevKey) => prevKey + 1);
                            toaster.toastSuccess("Card added successfully.");
                        }
                        else
                        {
                            // show error message
                            toaster.toastError(data?.message);
                        }
                        } catch (error: any) {
                            toaster.toastError(error?.response?.data?.message || error?.message);
                        }
                        finally{
                            setLoading(false);
                        }
                }
            } catch (error) {
                setLoading(false);
                //If validation fails on submit, then errors are shown
                if (error instanceof Yup.ValidationError) {

                    const newErrors: any = {};
                    error.inner.forEach((err) => {
                        newErrors[err.path as keyof typeof formData] = err.message;
                    });
                    // setErrors(newErrors);
                    setCardErrors(newErrors)
                }
            }
        }


    };


    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // To allow only digits in phone number field
        if (event.target.id === "custphno") {
            // Extract only digits from the input value
            const phone_value = event.target.value;
            if (!/^\d*$/.test(phone_value)) {
                event.target.value = phone_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in cvv field
        if (event.target.id === "custcvv") {
            // Extract only digits from the input value
            const cvv_value = event.target.value;
            if (!/^\d*$/.test(cvv_value)) {
                event.target.value = cvv_value.replace(/\D/g, '');
            }
        }

        const { id, value } = event.target
        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(id)) {
            setErrors({
                ...errors,
                [id]: ""
            })
        }

        setFormData({
            ...formData,
            [id]: value,
        });

        // Change user enters Phone number in usa format
        if (id === 'custcardnumber') {
            const formattedValue = formatCreditCardNumber(value);
            setFormData({
                ...formData,
                [id]: formattedValue,
            });
        }
        // Change user enters expiry date chane format
        if (id === 'custexpirydate') {
            const formattedValue = formatExpirationDate(value);
            setFormData({
                ...formData,
                [id]: formattedValue,
            });
        }

    }



    const addnewCardHandle = () => {
        setErrors({
            // custpromocode: '',
            custcardnumber: '',
            custexpirydate: '',
            custcvv: '',
            custcardname: '',
        });
        setOpen(!open);
    }

    useEffect(() => {
        const scrollIntoViewIfNeeded = () => {
            if (open) {
                const element = document.getElementById('card-form-collapse');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                const currentInput = document.getElementById('custcardnumber');
                if (currentInput) {
                    currentInput.focus();
                }
            }
        };

        // Delay scroll until the Collapse animation completes (about 300ms)
        if (open) {
            setTimeout(scrollIntoViewIfNeeded, 300);
        }
    }, [open]);

    const [showEditCardModal, setShowEditCardModal] = useState(false);
    const handleCloseEditCardModal = () => setShowEditCardModal(false);
    const [editcardid,setEditcardid] =useState("");
    const [editcardnumber,setEditcardnumber] =useState("");
    const [edit_exp_month,setEdit_exp_month] =useState(0);
    const [edit_exp_year,setEdit_exp_year] =useState(0);
    const [edit_default, setEdit_default] =useState(0);
    const handleShowEditCardModal = (editcardid : string, editcardnumber : string, edit_exp_month : number, edit_exp_year : number, edit_default: boolean ) => {
        setOpen(false);
        setShowEditCardModal(true);
        setEditcardid(editcardid)
        setEditcardnumber(editcardnumber);
        setEdit_exp_month(edit_exp_month);
        setEdit_exp_year(edit_exp_year);
        setEdit_default(edit_default === true ? 1 : 0);
    };
    const [isnewadded, setIsnewadded] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    useEffect(() => {
        getSavedCards();
    }, [showEditCardModal,isnewadded]);


    return (
        <div className="common-content-wrap pb-115">
            <div className="inner-content-wrap">
                <div className="common-header two-block-header">
                    <div className="pe-4 ps-4 pt-0 pb-0 h-100">
                        <div className="common-header-inner">
                            {/** If redirecting from promotions page then go bak to promotion page itself */}
                            <Link to="#" onClick={() => navigate(-1)} title="Car Wash App" className="back-link"><img src={Gobackicon} alt="Car Wash App" /></Link> Payment Info
                        </div>
                    </div>
                </div>
                <div className="main-content-wrap pe-0 ps-0 pt-0">
                    <div className="payment-content-sec">
                    {
                        savedcards.length > 0 && (
                            <div className="card-details">
                            <div className="title-sec d-flex align-items-center">
                                <span><img src={Cardicon} alt="Car Wash App" /></span>
                                <h6>Saved Cards</h6>
                            </div>
                            <div className="edit-card-group d-flex flex-column">
                                {savedcards.map((card, index) => (
                                <div key={card.cardId} className="edit-card d-flex align-items-center justify-content-between">
                                    <div className="card-info d-flex align-items-center">
                                    <p>**** **** **** {card.last4}</p>
                                    {card.default === true && <span className="badge">Primary</span>}
                                    </div>
                                    <Button
                                    variant="light"
                                    className="edit-btn"
                                    onClick={() => handleShowEditCardModal(card.cardId,card.last4,card.exp_month,card.exp_year,card.default)}
                                    >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17.873" height="17.958" viewBox="0 0 17.873 17.958">
                                        <defs>
                                        <clipPath id="clip-path">
                                            <rect width="17.873" height="17.958" fill="#0182f0" />
                                        </clipPath>
                                        </defs>
                                        <g clipPath="url(#clip-path)">
                                        <path
                                            d="M14.351,1.185a.948.948,0,0,1,.675.279l1.345,1.345a.954.954,0,0,1,0,1.349L5.66,14.869a.7.7,0,0,1-.288.173L1.6,16.192l1.257-3.9L13.677,1.464a.947.947,0,0,1,.675-.279m0-1.185a2.132,2.132,0,0,0-1.513.626L1.945,11.52a.771.771,0,0,0-.189.309L.021,17.221a.439.439,0,0,0,.107.445.385.385,0,0,0,.385.1l5.2-1.586a1.879,1.879,0,0,0,.781-.469L17.209,5a2.139,2.139,0,0,0,0-3.025L15.864.626A2.132,2.132,0,0,0,14.351,0"
                                            fill="#0182f0"
                                        />
                                        <path
                                            d="M20.2,35.653l7.07-7.069a.741.741,0,0,0,0-1.048l-.315-.315a.741.741,0,0,0-1.048,0l-7.07,7.069a.741.741,0,0,0,0,1.048l.315.315a.741.741,0,0,0,1.048,0"
                                            transform="translate(-14.941 -21.67)"
                                            fill="#0182f0"
                                        />
                                        <path
                                            d="M.655,85.975H17.3a.61.61,0,1,0,0-1.219H.655a.61.61,0,0,0,0,1.219"
                                            transform="translate(-0.036 -68.018)"
                                            fill="#0182f0"
                                        />
                                        <path
                                            d="M65.581,15.011l.981-.981a.741.741,0,0,0,0-1.048l-.315-.315a.741.741,0,0,0-1.048,0l-.981.981a.741.741,0,0,0,0,1.048l.315.315a.741.741,0,0,0,1.048,0"
                                            transform="translate(-51.361 -9.991)"
                                            fill="#0182f0"
                                        />
                                        </g>
                                    </svg>
                                    </Button>
                                </div>
                                ))}
                            </div>
                            </div>
                        )
                    }

                        {savedcards.length >0 ? <Button
                            variant="light"
                            className="p-0 add-btn d-flex align-items-center"
                            onClick={addnewCardHandle}
                            aria-controls="card-form-collapse"
                            aria-expanded={open}

                        >
                            <img src={Addicon} alt="Car Wash App" title='Add New Card' /> Add New Card
                        </Button> : ""}
                        <Collapse in={open}>


                            <div className="card-details mt-3" id="card-form-collapse">
                                <div className="title-sec d-flex align-items-center position-relative">
                                        <span><img src={Cardicon} alt="Car Wash App" /></span>
                                        <h6>Enter Card Details</h6>
                                    <div className="location-info-card setprimary-btn m-0">
                                    <label className="radio-wrap m-0">
                                    <input
                                        name="isDefault"
                                        type="checkbox"
                                        className='input-radio'
                                        value={isdefaultcard}
                                        checked={isdefaultcard === 1 ? true : false}
                                        onChange={(e) => handleCheckboxChange(e.target.checked)}
                                    />
                                        <span>
                                            <div className="text-info">
                                                <p>Set as Primary Card</p>
                                            </div>
                                        </span>
                                    </label>
                                </div>
                                </div>
                                <div className="card-form">
                                    <div className="form-group cw-input">
                                        <label>Card Number</label>
                                        <span className="mandatory">*</span>
                                        <CardNumberElement key={resetKey} className="form-control" onChange={handleCardNumberChange} options={cardTypeOption}/>
                                        {cardErrors.custcardnumber && <span className="error-msg">{cardErrors.custcardnumber}</span>}
                                    </div>
                                    <div className="multi-field">
                                        <div className="form-group cw-input">
                                            <label>Expiry Date</label>
                                            <span className="mandatory">*</span>
                                            <CardExpiryElement key={resetKey} className="form-control" onChange={handleCardExpiryChange} />
                                            {cardErrors.custexpirydate && <span className="error-msg">{cardErrors.custexpirydate}</span>}
                                        </div>
                                        <div className="form-group cw-input">
                                            <label>CVC</label>
                                            <span className="mandatory">*</span>
                                            <CardCvcElement key={resetKey} className="form-control" onChange={handleCardCvcChange} />
                                            {cardErrors.custcvv && <span className="error-msg">{cardErrors.custcvv}</span>}
                                        </div>
                                    </div>
                                    <CWInput
                                        id="custcardname"
                                        name="custcardname"
                                        type="text"
                                        label="Name on Card"
                                        value={formData.custcardname}
                                        placeholder='Name on Card'
                                        errorMessage={cardErrors.custcardname && cardErrors.custcardname}
                                        maxLength={NAME_LENGTH}
                                        onChange={handleInputChange}
                                    />
                                   
                                    {success && <div className="success">Card saved successfully!</div>}
                                    {/* </form> */}
                                    <Button variant="primary" className="primary-btn btn-main" onClick={handleSubmit}>Save</Button>
                                </div>
                            </div>
                        </Collapse>


                    </div>
                </div>
            </div>
            <EditCardModal
                show={showEditCardModal}
                onHide={handleCloseEditCardModal}
                editcardid={editcardid}
                editcardnumber={editcardnumber}
                edit_exp_month={edit_exp_month}
                edit_exp_year={edit_exp_year}
                edit_default={edit_default}
            />
            {loading ? <SmallLoader/> : ""}
            {statusCheckingLoader ? <FullLoaderwithText/> : ""}
        </div>
        
    );
};

export default StripeUpdatePaymentCard;
